import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { listingCheckAPI } from '../../../../services/api';
import type { ListingCheckResult, CheckItemUpdate, CreateItemPayload } from './types';

export const useListingCheck = (skuId?: number) => {
    const [activeSite, setActiveSite] = useState<string>('US');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<ListingCheckResult[]>([]);
    const [saving, setSaving] = useState(false);

    // 加载数据
    const loadResults = async () => {
        if (!skuId) return;
        setLoading(true);
        try {
            const res = await listingCheckAPI.initResults(skuId, activeSite);
            if (res.data) {
                setResults(res.data);
            }
        } catch (error) {
            message.error('加载检查项失败');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadResults();
    }, [skuId, activeSite]);

    // 修改本地状态
    const handleUpdateResult = (itemId: number, updates: CheckItemUpdate) => {
        setResults(prev => prev.map(r => {
            if (r.item === itemId) {  // Note: r.item is the Item ID (foreign key conceptually)
                return { ...r, ...updates };
            }
            return r;
        }));
    };

    // 保存所有
    const handleSave = async () => {
        if (!skuId) return;
        setSaving(true);
        try {
            // Prepare payload
            const payload = {
                sku_id: skuId,
                site: activeSite,
                results: results.map(r => ({
                    item_id: r.item,
                    is_compliant: r.is_compliant,
                    detected_value: r.value_detected,
                    note: r.note
                }))
            };

            await listingCheckAPI.bulkSave(payload);
            message.success('保存成功');
        } catch (error) {
            message.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    // 添加自定义项
    const addItem = async (values: CreateItemPayload) => {
        try {
            await listingCheckAPI.createItem({
                ...values,
                is_default: false,
                is_active: true
            });
            message.success('添加成功');
            loadResults(); // Reload
            return true;
        } catch (error) {
            message.error('添加失败');
            return false;
        }
    };

    // 计算进度
    const progress = useMemo(() => {
        if (results.length === 0) return 0;
        const compliantCount = results.filter(r => r.is_compliant).length;
        return Math.round((compliantCount / results.length) * 100);
    }, [results]);

    // Group by category
    const groupedResults = useMemo(() => {
        const groups: Record<string, ListingCheckResult[]> = {};
        results.forEach(r => {
            const cat = r.item_info.category;
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(r);
        });
        return groups;
    }, [results]);

    return {
        activeSite,
        setActiveSite,
        loading,
        results,
        saving,
        progress,
        groupedResults,
        updateResult: handleUpdateResult,
        saveResults: handleSave,
        addItem,
        reload: loadResults,
    };
};
